import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export interface DateValidation {
  date: string;
  isValid: boolean;
  error?: string;
}

export const useBookingValidation = (selectedTrackType: 'drag' | 'drift', userProfile: { role: string } | null) => {
  const [dateValidations, setDateValidations] = useState<DateValidation[]>([]);
  const [validatingDates, setValidatingDates] = useState(false);
  const [timeError, setTimeError] = useState<string>('');

  // Helper function to format date consistently (YYYY-MM-DD format)
  const formatDateForDatabase = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Force local timezone interpretation
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Force local timezone interpretation
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to create Date objects for time comparison
  const createTimeDate = (timeString: string, isEndTime: boolean = false, startTimeString?: string): Date => {
    if (!timeString) return new Date();
    
    const baseDate = new Date('2000-01-01');
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeDate = new Date(baseDate);
    timeDate.setHours(hours, minutes, 0, 0);
    
    // If this is an end time and it's before or equal to start time, it's next day
    if (isEndTime && startTimeString) {
      const [startHours, startMinutes] = startTimeString.split(':').map(Number);
      const startDate = new Date(baseDate);
      startDate.setHours(startHours, startMinutes, 0, 0);
      
      if (timeDate <= startDate) {
        timeDate.setDate(timeDate.getDate() + 1);
      }
    }
    
    return timeDate;
  };

  // Helper function to check if two time ranges overlap
  const doTimesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
    const start1Date = createTimeDate(start1);
    const end1Date = createTimeDate(end1, true, start1);
    const start2Date = createTimeDate(start2);
    const end2Date = createTimeDate(end2, true, start2);
    
    // Check for overlap: start1 < end2 && start2 < end1
    return start1Date < end2Date && start2Date < end1Date;
  };

  // Helper function to check if a date is within 7 days of any event date
  const checkEventBufferConflict = (bookingDate: string, eventDates: string[]): { hasConflict: boolean; conflictDate?: string } => {
    const bookingDateObj = new Date(bookingDate + 'T00:00:00');
    
    for (const eventDate of eventDates) {
      const eventDateObj = new Date(eventDate + 'T00:00:00');
      const daysDifference = Math.abs((bookingDateObj.getTime() - eventDateObj.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDifference <= 7) {
        return { hasConflict: true, conflictDate: eventDate };
      }
    }
    
    return { hasConflict: false };
  };

  const validateSingleDate = async (date: string, selectedStartTime?: string, selectedEndTime?: string, selectedPricing?: any): Promise<DateValidation> => {
    if (!date.trim()) {
      return { date, isValid: true };
    }

    try {
      // Format date consistently for database queries
      const formattedDate = formatDateForDatabase(date);

      // Check for events on this date
      const { data: eventDates, error: eventError } = await supabase
        .from('event_dates')
        .select('*')
        .eq('event_date', formattedDate);

      if (eventError) throw eventError;

      if (eventDates && eventDates.length > 0) {
        return {
          date,
          isValid: false,
          error: `Event scheduled on ${formatDateForDisplay(date)}`
        };
      }

      // Additional validation for promotor users - check 7-day buffer around events
      if (userProfile?.role === 'promotor') {
        const { data: allEventDates, error: allEventsError } = await supabase
          .from('event_dates')
          .select('event_date');

        if (allEventsError) throw allEventsError;

        if (allEventDates && allEventDates.length > 0) {
          const eventDateStrings = allEventDates.map(ed => ed.event_date);
          const bufferCheck = checkEventBufferConflict(formattedDate, eventDateStrings);
          
          if (bufferCheck.hasConflict) {
            return {
              date,
              isValid: false,
              error: `Promotor bookings require a 7-day buffer around events. Event on ${formatDateForDisplay(bufferCheck.conflictDate!)}`
            };
          }
        }
      }

      // If we have time selection, check for time conflicts
      if (selectedStartTime && selectedEndTime && selectedPricing) {
        const { data: existingBookings, error: bookingError } = await supabase
          .from('track_bookings')
          .select('start_time, end_time, track_type, id')
          .eq('track_type', selectedTrackType)
          .eq('booking_date', formattedDate)
          .neq('status', 'cancelled');

        if (bookingError) throw bookingError;

        if (existingBookings && existingBookings.length > 0) {
          // Check for time overlap using improved logic
          const newStartTime = selectedStartTime;
          const newEndTime = selectedEndTime;

          for (const booking of existingBookings) {
            if (booking.start_time && booking.end_time) {
              // Remove seconds from existing booking times for comparison
              const existingStart = booking.start_time.substring(0, 5);
              const existingEnd = booking.end_time.substring(0, 5);
              
              if (doTimesOverlap(newStartTime, newEndTime, existingStart, existingEnd)) {
                return {
                  date,
                  isValid: false,
                  error: `Time conflict on ${formatDateForDisplay(date)}: ${formatTime(existingStart)} - ${formatTime(existingEnd)}`
                };
              }
            }
          }
        }
      } else {
        // For date-only validation (no time selected yet), check if there are any bookings on this date
        const { data: existingBookings, error: bookingError } = await supabase
          .from('track_bookings')
          .select('id, track_type')
          .eq('track_type', selectedTrackType)
          .eq('booking_date', formattedDate)
          .neq('status', 'cancelled');

        if (bookingError) throw bookingError;

        if (existingBookings && existingBookings.length > 0) {
          return {
            date,
            isValid: false,
            error: `${formatDateForDisplay(date)} already has bookings - time conflicts may occur`
          };
        }
      }

      return { date, isValid: true };
    } catch (error) {
      console.error('Error validating date:', error);
      return {
        date,
        isValid: false,
        error: 'Failed to validate date'
      };
    }
  };

  const validateAllDates = async (
    selectedDates: string[], 
    selectedStartTime?: string, 
    selectedEndTime?: string, 
    selectedPricing?: any
  ) => {
    setValidatingDates(true);
    const validations: DateValidation[] = [];

    for (const date of selectedDates) {
      if (!date.trim()) {
        validations.push({ date, isValid: true });
        continue;
      }

      try {
        const validation = await validateSingleDate(date, selectedStartTime, selectedEndTime, selectedPricing);
        validations.push(validation);
      } catch (error) {
        validations.push({
          date,
          isValid: false,
          error: 'Failed to validate date'
        });
      }
    }

    setDateValidations(validations);
    setValidatingDates(false);
    return validations;
  };

  const validateTimeSelection = (
    selectedStartTime: string, 
    selectedEndTime: string, 
    selectedPricing: any
  ) => {
    if (!selectedStartTime || !selectedEndTime || !selectedPricing) {
      setTimeError('');
      return;
    }

    const startDate = createTimeDate(selectedStartTime);
    const endDate = createTimeDate(selectedEndTime, true, selectedStartTime);

    const actualDurationMs = endDate.getTime() - startDate.getTime();
    const actualDurationHours = actualDurationMs / (1000 * 60 * 60);
    const expectedDurationHours = selectedPricing.duration_hours;
    
    // Allow 5 minutes tolerance
    const toleranceHours = 5 / 60; // 5 minutes in hours
    
    if (Math.abs(actualDurationHours - expectedDurationHours) > toleranceHours) {
      setTimeError(
        `Time range duration (${actualDurationHours.toFixed(1)}h) doesn't match selected package (${expectedDurationHours}h). Please adjust your times.`
      );
      return false;
    } else {
      setTimeError('');
      return true;
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const hasInvalidDates = () => {
    return dateValidations.some(validation => !validation.isValid && validation.date.trim() !== '');
  };

  const getInvalidDatesMessage = () => {
    const invalidDates = dateValidations.filter(validation => !validation.isValid && validation.date.trim() !== '');
    if (invalidDates.length === 0) return '';
    
    return `Please fix the following dates: ${invalidDates.map(d => formatDateForDisplay(d.date)).join(', ')}`;
  };

  const getDateValidation = (date: string) => {
    return dateValidations.find(validation => validation.date === date);
  };

  return {
    dateValidations,
    validatingDates,
    timeError,
    validateAllDates,
    validateTimeSelection,
    formatTime,
    hasInvalidDates,
    getInvalidDatesMessage,
    getDateValidation,
    setTimeError,
    formatDateForDatabase,
    formatDateForDisplay
  };
};